import { Popover } from '@/components/utils/Popover/Popover';
import { Box, Stack, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { SlideComponentFileModel, SlideComponentHkcsEnQ1 } from '@/types/slide';
import { useRef, useState } from 'react';

import { ImagePicker } from '@/components/inputs/ImagePicker/ImagePicker';
import { BUILTIN_IMG_SRC } from '@/components/inputs/ImagePicker/source';
import { CANVAS_SIZE } from '@/constant';
import { AudioRecorderPopover } from '../../audio/AudioRecorderPopover';
import { AudioSelector } from '../components/AudioSelector';
import { ImageSelector } from '../components/ImageSelector';

import { EraserFilledIcon } from '@/components/displays/CustomIcons';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PaletteIcon from '@mui/icons-material/Palette';
import { ButtonPlaceholder } from '../components/ButtonPlaceholder';

import React from 'react';
import { useDebounce } from 'rooks';

interface Props {
    anchorEl: HTMLElement | null;
    value: SlideComponentHkcsEnQ1;
    slotProps?: { paper?: { ref: React.Ref<HTMLDivElement> } };
    onClose: () => void;
    onUpdate?: (model: SlideComponentHkcsEnQ1) => void;
    onUploadImage?: (file: File) => Promise<string>;
    onUploadAudio?: (file: File) => Promise<SlideComponentFileModel | null>;
}

export const EditPopover: React.FC<Props> = ({ anchorEl, value, slotProps, onClose, onUpdate, onUploadImage, onUploadAudio }) => {
    const { t } = useTranslation();
    const open = Boolean(anchorEl);

    // ref
    const imagePickerRef = useRef<HTMLDivElement | null>(null);

    const [audioRecorderAnchorEl, setAudioRecorderAnchorEl] = useState<HTMLElement | null>(null);

    // ui state
    const [imagePickerAnchorEl, setImagePickerAnchorEl] = useState<HTMLElement | null>(null);

    const debouncedUpdate = useDebounce(onUpdate || (() => {}), 500);

    return (
        <>
            <Popover
                title={t('slide.legacy.hkcsEnQ1.title')}
                open={open}
                anchorEl={anchorEl}
                onClose={() => {
                    onClose();
                }}
                anchorOrigin={{
                    vertical: 'center',
                    horizontal: 'center',
                }}
                transformOrigin={{
                    vertical: 'center',
                    horizontal: 'center',
                }}
                slotProps={{
                    paper: {
                        ref: slotProps?.paper?.ref,
                        sx: {
                            display: 'flex',
                            px: 5,
                            py: 2,
                            width: CANVAS_SIZE.width,
                            height: CANVAS_SIZE.height,
                            overflow: 'auto',
                        },
                    },
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flex: 1,
                        gap: 1,
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            width: 440,
                            height: 576,
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            backgroundColor: 'white',
                            borderRadius: 2,
                        }}
                    >
                        <Box sx={{ width: 338, margin: 2 }}>
                            <ImageSelector
                                src={value.content.imageURL ? value.content.imageURL : value.content.image ? `/asset/characters/${value.content.image}/image.png` : undefined}
                                onSelect={(_, selectorRef) => {
                                    setImagePickerAnchorEl(selectorRef.current);
                                }}
                            />
                        </Box>

                        <Box sx={{ width: 64 }}>
                            <AudioSelector
                                src={value.content?.audio?.[0]?.url}
                                onSelect={(_, selectorRef) => {
                                    setAudioRecorderAnchorEl(selectorRef.current);
                                }}
                            />
                        </Box>

                        <Box
                            sx={{
                                display: 'flex',
                                width: 376,
                                height: 131,
                                justifyContent: 'center',
                                alignItems: 'center',
                            }}
                        >
                            <TextField
                                label={t('slide.legacy.hkcsEnQ1.label.phrase')}
                                slotProps={{
                                    input: {
                                        inputProps: {
                                            style: {
                                                textAlign: 'center',
                                            },
                                        },
                                    },
                                }}
                                fullWidth
                                sx={{
                                    mt: 1,
                                }}
                                defaultValue={value.content.phrase}
                                onChange={(e) => {
                                    debouncedUpdate({
                                        ...value,
                                        content: {
                                            ...value.content,
                                            phrase: e.target.value,
                                        },
                                    });
                                }}
                            />
                        </Box>
                    </Box>
                    <Box
                        sx={{
                            display: 'flex',
                            height: 576,
                            flexDirection: 'column',
                            justifyContent: 'space-between',
                        }}
                    >
                        <Box
                            sx={(theme) => ({
                                display: 'flex',
                                width: 440,
                                height: 424,
                                justifyContent: 'center',
                                backgroundColor: 'white',
                                border: 2,
                                borderRadius: 2,
                                borderColor: (theme.vars || theme).palette.grey[200],
                            })}
                        />

                        <Box
                            sx={(theme) => ({
                                display: 'flex',
                                width: 440,
                                height: 131,
                                justifyContent: 'center',
                                backgroundColor: 'white',
                                border: 2,
                                borderRadius: 2,
                                borderColor: (theme.vars || theme).palette.grey[200],
                            })}
                        />
                    </Box>

                    <Stack
                        direction='column'
                        spacing={1}
                        sx={{
                            height: 576,
                        }}
                    >
                        <ButtonPlaceholder icon={<PaletteIcon sx={{ color: 'divider' }} fontSize='large' />} />
                        <ButtonPlaceholder icon={<EraserFilledIcon sx={{ color: 'divider' }} fontSize='large' />} />
                        <ButtonPlaceholder icon={<DeleteOutlineIcon sx={{ color: 'divider' }} fontSize='large' />} />
                    </Stack>
                </Box>
            </Popover>

            {imagePickerAnchorEl && (
                <ImagePicker
                    type='image'
                    title={t('slide.legacy.character.title')}
                    librarySources={BUILTIN_IMG_SRC}
                    anchorEl={imagePickerAnchorEl}
                    slotProps={{
                        paper: {
                            ref: imagePickerRef, // Attach the ref to the Paper element
                        },
                    }}
                    value={{
                        value: value.content.image,
                        imageURL: value.content.imageURL,
                    }}
                    onClose={() => {
                        setImagePickerAnchorEl(null);
                    }}
                    onUpdate={(settings) => {
                        const updatedContent: SlideComponentHkcsEnQ1['content'] = {
                            ...value.content,
                            image: settings.value,
                            imageURL: settings.imageURL,
                        };
                        const updateModel = { ...value, content: updatedContent, ...(settings.size ? { size: settings.size } : { size: value.size }) };

                        debouncedUpdate(updateModel);
                    }}
                    onUpload={onUploadImage}
                />
            )}

            {audioRecorderAnchorEl && (
                <AudioRecorderPopover
                    title={t('slide.asset.audio')}
                    anchorEl={audioRecorderAnchorEl}
                    value={{
                        id: value.content?.audio[0]?.id ?? '',
                        filename: value.content?.audio[0]?.filename ?? '',
                        url: value.content?.audio[0]?.url ?? '',
                    }}
                    onClose={() => {
                        setAudioRecorderAnchorEl(null);
                    }}
                    onUpdate={(audio) => {
                        const updatedContent: SlideComponentHkcsEnQ1['content'] = {
                            ...value.content,
                            audio: [audio],
                        };
                        const updateModel = { ...value, content: updatedContent };

                        debouncedUpdate(updateModel);
                    }}
                    onUpload={onUploadAudio}
                />
            )}
        </>
    );
};
